import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBalanceScale,
  faBoxesStacked,
  faCalculator,
  faCashRegister,
  faCheckCircle,
  faCircleNodes,
  faFileInvoiceDollar,
  faIndianRupeeSign,
  faShieldHalved,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import "./BillingSoftware.css";
import "./IndiaBusinessSoftware.css";

const path = "/india";
const title = "Business Software India | Auditus-iERP";
const description =
  "Auditus-iERP business software for India connects ERP, billing, accounting, inventory, POS, GST, sales, purchases, customers, suppliers, payments, and reports.";

const indiaModules = [
  [faCircleNodes, "ERP software in India", "Connected billing, accounting, inventory, POS, GST, sales, purchases, and reports.", "/erp-software"],
  [faFileInvoiceDollar, "Billing software in India", "GST-ready invoice workflows with products, customers, payments, and sales records.", "/billing-software"],
  [faCalculator, "Accounting software for Indian businesses", "Ledgers, vouchers, expenses, payments, receivables, payables, and financial reports.", "/accounting-software"],
  [faBoxesStacked, "Inventory management software in India", "Products, stock tracking, barcode movement, warehouses, batches, reorder levels, and item reports.", "/inventory-management-software"],
  [faCashRegister, "POS software for Indian retail", "Fast retail checkout with POS billing, barcode support, customers, payments, and stock connection.", "/pos-software"],
  [faBalanceScale, "GST software for Indian businesses", "GST-ready invoices, GSTIN details, HSN/SAC handling, tax calculation workflows, and GST reports.", "/gst-software"],
];

const solutionSections = [
  {
    title: "ERP Software in India",
    route: "/erp-software",
    linkText: "Explore ERP software",
    text:
      "Businesses comparing ERP software India or ERP software for small business India should look for connected operations: billing, accounting, inventory, sales, purchases, and reporting. Auditus-iERP addresses these areas through its integrated business management modules without making unsupported ranking claims.",
  },
  {
    title: "Billing Software for Businesses in India",
    route: "/billing-software",
    linkText: "Explore billing software",
    text:
      "For billing software India searches, buyers usually need invoice generation, GST billing, product management, customer information, payment records, and reports. Auditus-iERP supports GST-ready billing with products, customers, payments, inventory connection, barcode workflows, and sales reports.",
  },
  {
    title: "Accounting Software for Indian Businesses",
    route: "/accounting-software",
    linkText: "Explore accounting software",
    text:
      "Accounting software in India should help teams organize transactions, ledgers, expenses, payments, receivables, payables, GST-related records, and reports. Auditus-iERP connects accounting with billing, inventory, purchases, sales, and GST workflows.",
  },
  {
    title: "Inventory Management Software in India",
    route: "/inventory-management-software",
    linkText: "Explore inventory software",
    text:
      "Inventory software India buyers should check product records, stock tracking, purchases, sales, barcode movement, warehouses, batches, reorder levels, and reports. Auditus-iERP supports these inventory workflows and connects them with billing and accounts.",
  },
  {
    title: "POS Software for Indian Retail Businesses",
    route: "/pos-software",
    linkText: "Explore POS software",
    text:
      "Retail POS software India buyers need fast checkout, billing, product selection, barcode support, inventory connection, payment handling, customers, and reports. Auditus-iERP supports POS billing for retail, supermarkets, grocery, and textile workflows.",
  },
  {
    title: "GST Software for Indian Businesses",
    route: "/gst-software",
    linkText: "Explore GST software",
    text:
      "GST software India buyers should check GST invoice creation, tax calculation workflows, GSTIN details, HSN/SAC, GST reports, and billing/accounting integration. Auditus-iERP supports GST-ready invoices, HSN/SAC handling, GST reports, e-invoice workflows, and e-way bill workflows.",
  },
];

const industries = [
  ["Supermarkets", "Fast billing, POS, barcode workflows, products, inventory, payments, GST-ready invoices, and reports.", "POS + Billing + Inventory + GST + Reports"],
  ["Retail stores", "Retail billing, product management, customer records, payments, stock visibility, and sales reporting.", "POS + Billing + Inventory"],
  ["Grocery businesses", "Product billing, purchase records, stock visibility, customers, sales tracking, and business reports.", "Billing + Inventory + Reports"],
  ["Textile businesses", "Billing, product and stock management, purchases, accounting, GST workflows, customers, suppliers, and reports.", "Billing + Inventory + Accounting + GST"],
  ["Wholesale businesses", "Sales, purchases, inventory, customer and supplier records, payments, accounting, and reports.", "Billing + Inventory + Accounting"],
  ["Distributors", "Billing, inventory, purchases, customers, suppliers, payment references, and business reporting.", "Billing + Purchases + Inventory + Reports"],
  ["Small businesses", "Easy billing, accounting, inventory, GST workflows, customers, payments, and reports in one organized system.", "ERP + Billing + Accounting + Inventory"],
];

const workflow = ["Purchase", "Inventory", "Sales", "Billing", "GST", "Payment", "Accounting", "Reports"];

const indiaChecks = [
  ["GST requirements", "Check GST-ready invoices, tax calculation workflows, GSTIN details, HSN/SAC, and GST reports."],
  ["Billing", "Check invoice creation, product selection, customer records, payment recording, and sales reporting."],
  ["Accounting", "Check ledgers, vouchers, expenses, payments, receivables, payables, and financial reports."],
  ["Inventory", "Check products, stock tracking, purchases, sales, barcode workflows, warehouses, batches, and reorder levels."],
  ["POS", "Retail businesses should check checkout speed, barcode product selection, payment handling, and inventory connection."],
  ["Reporting", "Check sales, purchase, inventory, GST, financial, audit, item-wise, and dashboard reports."],
  ["Pricing/value", "Check whether the plan, users, support, and modules fit your business needs."],
  ["Support and fit", "Check whether the software works for your business type and day-to-day staff workflow."],
];

const whyIndia = [
  "GST-related billing and invoice workflows",
  "Business accounting with ledgers, vouchers, payments, expenses, and reports",
  "Inventory management for products, stock, purchases, sales, barcode movement, warehouses, and batches",
  "Retail and POS workflows for checkout-focused businesses",
  "Sales and purchase records connected with customers, suppliers, payments, and reports",
  "Business reporting across sales, purchase, inventory, GST, financial, audit, item-wise, and dashboard views",
  "Indian Rupee (INR) pricing is available on the existing pricing page",
];

const screenshots = [
  ["/images/screenshots/dashboard.png", "Auditus-iERP ERP software for Indian businesses", "ERP dashboard"],
  ["/images/screenshots/invoice.png", "Auditus-iERP GST billing software India", "Billing and POS screen"],
  ["/images/screenshots/reports.png", "Auditus-iERP accounting software India reports screen", "Accounting and reports"],
  ["/images/gstdashboard.PNG", "Auditus-iERP GST software dashboard for India", "GST dashboard"],
];

const faqs = [
  ["Is Auditus-iERP available for businesses in India?", "Yes. Auditus-iERP is presented in this project as business software for businesses operating in India, with billing, accounting, inventory, POS, GST workflows, purchases, sales, customers, suppliers, payments, and reports."],
  ["What is the best ERP software for businesses in India?", "The right ERP software depends on your business requirements. Indian businesses should evaluate billing, accounting, inventory, GST, sales, purchases, reporting, ease of use, support, and pricing. Auditus-iERP supports these connected workflows."],
  ["What is the best billing software in India?", "The right billing software depends on your workflow. Auditus-iERP supports GST-ready invoices, product selection, customer details, payment recording, barcode workflows, inventory connection, and sales reports."],
  ["What is the best accounting software in India?", "A good accounting system should support ledgers, vouchers, expenses, payments, receivables, payables, GST-related records, and reports. Auditus-iERP includes these accounting workflows."],
  ["What is the best inventory management software in India?", "The right inventory software should support product management, stock tracking, purchases, sales, barcode movement, warehouses, batches, reorder levels, and reports. Auditus-iERP supports these inventory workflows."],
  ["What is the best POS software in India?", "Retail POS software should support fast checkout, billing, products, barcode support, payments, customers, inventory connection, and reports. Auditus-iERP supports these POS workflows."],
  ["Does Auditus-iERP support GST billing?", "Yes. Auditus-iERP supports GST-ready invoices, tax calculation workflows, GSTIN details, HSN/SAC handling, GST reports, e-invoice workflows, and e-way bill workflows."],
  ["Can Indian businesses manage billing and accounting together?", "Yes. Auditus-iERP connects billing and accounting through invoices, payments, ledgers, vouchers, financial reports, and GST-related records."],
  ["Can Indian businesses manage inventory and billing together?", "Yes. Auditus-iERP connects inventory and billing through products, stock tracking, purchases, sales, barcode movement, and item-wise reports."],
  ["Can retail businesses in India use Auditus-iERP?", "Yes. Retail businesses can use POS billing, barcode workflows, products, customers, payments, inventory, GST workflows, and reports."],
  ["Can supermarkets in India use Auditus-iERP?", "Yes. Supermarkets can use fast billing, POS, barcode workflows, products, inventory, purchases, sales tracking, payments, GST-ready invoices, and reports."],
  ["Can textile businesses in India use Auditus-iERP?", "Yes. Textile businesses can use billing, product and stock management, purchases, accounting, GST workflows, customers, suppliers, and reports."],
  ["Is Auditus-iERP suitable for small businesses in India?", "Yes. Small businesses can use Auditus-iERP for easy billing, accounting, inventory, GST workflows, customer and supplier records, payments, and business reports."],
  ["How can I get an Auditus-iERP demo?", "Use the Book a Demo button to open the demo request form and share your business requirements."],
];

function setMeta(name, content, attribute = "name") {
  let tag = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

export default function IndiaBusinessSoftware() {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    setMeta("description", description);
    setMeta("robots", "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", path, "property");
    setMeta("og:image", "/images/screenshots/dashboard.png", "property");
    setLink("canonical", path);

    const schema = document.createElement("script");
    schema.id = "india-business-software-schema";
    schema.type = "application/ld+json";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "/" },
            { "@type": "ListItem", position: 2, name: "Business Software India", item: path },
          ],
        },
        {
          "@type": "SoftwareApplication",
          name: "Auditus-iERP Business Software India",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Windows, Android",
          url: path,
          image: "/images/screenshots/dashboard.png",
          description,
          featureList: indiaModules.map((item) => item[1]),
        },
        {
          "@type": "FAQPage",
          mainEntity: faqs.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
      ],
    });
    document.head.appendChild(schema);

    return () => {
      document.title = previousTitle;
      schema.remove();
    };
  }, []);

  return (
    <main className="billing-page india-page">
      <section className="india-hero" aria-labelledby="india-title">
        <div className="billing-container india-hero-grid">
          <div>
            <nav className="billing-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Business Software India</span>
            </nav>
            <p className="billing-kicker">
              <FontAwesomeIcon icon={faCircleNodes} aria-hidden="true" />
              Business software India
            </p>
            <h1 id="india-title">Business Software Built for Businesses in India</h1>
            <p className="billing-lead">
              Auditus-iERP helps Indian businesses manage billing, accounting,
              inventory, POS, GST workflows, sales, purchases, payments, customers,
              suppliers, and reports from one connected business software platform.
            </p>
            <div className="billing-actions">
              <Link to="/Demo" className="billing-btn billing-btn-primary">
                Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Trial" className="billing-btn billing-btn-secondary">
                Get Started
              </Link>
            </div>
            <div className="india-signals" aria-label="India business software signals">
              <span>GST workflows</span>
              <span>INR pricing page</span>
              <span>Retail and small business use</span>
            </div>
          </div>
          <div className="billing-window">
            <div className="billing-window-bar">
              <span>Auditus-iERP - India Business Software</span>
              <i />
            </div>
            <img
              src="/images/screenshots/dashboard.png"
              alt="Auditus-iERP ERP software for Indian businesses"
              width="920"
              height="560"
              loading="eager"
            />
            <div className="billing-window-foot">
              <span>Billing</span>
              <span>GST</span>
              <span>Stock</span>
              <span>Reports</span>
            </div>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="india-needs-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Indian business needs</p>
            <h2 id="india-needs-title">Business Software for the Needs of Indian Businesses</h2>
          </div>
          <div className="billing-copy">
            <p>
              Businesses in India often need GST-enabled billing, business accounting,
              inventory management, retail billing, POS, sales and purchase records,
              customer management, supplier management, payment tracking, and business reports.
            </p>
            <p>
              Auditus-iERP connects these verified modules in one platform so daily
              business information can move through billing, accounts, stock, GST, and reports.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section india-soft" aria-labelledby="modules-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">India-focused modules</p>
            <h2 id="modules-title">ERP, Billing, Accounting, Inventory, POS & GST Software in India</h2>
            <p>
              These are the main Auditus-iERP product areas relevant for Indian
              businesses comparing business management software.
            </p>
          </div>
          <div className="india-module-grid">
            {indiaModules.map(([icon, name, text, route]) => (
              <Link to={route} key={name}>
                <span className="billing-feature-icon">
                  <FontAwesomeIcon icon={icon} aria-hidden="true" />
                </span>
                <strong>{name}</strong>
                <p>{text}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="software-sections-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Software categories</p>
            <h2 id="software-sections-title">Business Software Categories for India</h2>
          </div>
          <div className="india-solution-stack">
            {solutionSections.map((section) => (
              <article key={section.title}>
                <h3>{section.title}</h3>
                <p>{section.text}</p>
                <Link to={section.route}>
                  {section.linkText} <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section india-industries" aria-labelledby="industries-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Indian business types</p>
            <h2 id="industries-title">Business Software for Different Industries in India</h2>
            <p>
              Auditus-iERP is relevant for Indian businesses that need connected billing,
              stock, accounts, GST workflows, POS, purchases, sales, and reports.
            </p>
          </div>
          <div className="india-industry-grid">
            {industries.map(([name, need, modules]) => (
              <article key={name}>
                <FontAwesomeIcon icon={faStore} aria-hidden="true" />
                <h3>{name}</h3>
                <p>{need}</p>
                <strong>{modules}</strong>
              </article>
            ))}
          </div>
          <div className="india-inline-cta">
            <Link to="/industries" className="billing-btn billing-btn-secondary">
              Explore industry solutions
            </Link>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="small-business-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Small business India</p>
            <h2 id="small-business-title">Business Software for Small Businesses in India</h2>
          </div>
          <div className="billing-copy">
            <p>
              Small businesses in India often need easy billing, accounting, inventory,
              GST workflows, customer management, supplier records, payment tracking,
              and practical reports without splitting work across many tools.
            </p>
            <p>
              Auditus-iERP is useful for small businesses that want these verified
              business workflows connected in one system. For pricing and user options,
              use the existing <Link to="/Pricing">pricing page</Link>.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section india-workflow-section" aria-labelledby="workflow-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Connected India workflow</p>
            <h2 id="workflow-title">From Purchase to Reports in One Business System</h2>
            <p>
              This workflow uses connections already supported across Auditus-iERP:
              purchases, inventory, sales, billing, GST workflows, payments, accounting,
              and reports.
            </p>
          </div>
          <div className="india-workflow" aria-label="Auditus-iERP India business workflow">
            {workflow.map((step, index) => (
              <React.Fragment key={step}>
                <div>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <strong>{step}</strong>
                </div>
                {index < workflow.length - 1 && <i aria-hidden="true" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="market-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">India market fit</p>
            <h2 id="market-title">Why Choose Business Software Designed for the Indian Market?</h2>
          </div>
          <div className="india-benefit-grid">
            {whyIndia.map((item) => (
              <div key={item}>
                <FontAwesomeIcon icon={item.includes("INR") ? faIndianRupeeSign : faCheckCircle} aria-hidden="true" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section india-dark" aria-labelledby="buyer-check-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Buyer checklist</p>
            <h2 id="buyer-check-title">What Should Indian Businesses Look for in Business Software?</h2>
            <p>
              Compare software based on the daily work your team actually performs,
              not only broad claims or generic feature lists.
            </p>
          </div>
          <div className="india-check-grid">
            {indiaChecks.map(([name, text]) => (
              <article key={name}>
                <h3>{name}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="screens-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Real product screenshots</p>
            <h2 id="screens-title">Auditus-iERP Screens for Indian Businesses</h2>
            <p>
              These are existing project assets. No fake India screenshots or mock
              interfaces are used.
            </p>
          </div>
          <div className="india-screenshot-grid">
            {screenshots.map(([src, alt, label]) => (
              <figure key={src}>
                <img src={src} alt={alt} loading="lazy" width="640" height="390" />
                <figcaption>{label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section india-soft" aria-labelledby="why-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Why Auditus-iERP for India</p>
            <h2 id="why-title">Why Choose Auditus-iERP for Your Business in India?</h2>
          </div>
          <div className="billing-copy">
            <p>
              Auditus-iERP is designed around integrated ERP, billing, accounting,
              inventory, POS, GST, sales, purchases, customers, suppliers, payments,
              expenses, and business reports.
            </p>
            <p>
              It is a practical fit for Indian businesses that want multiple business
              workflows connected in one product ecosystem. Compare related modules on
              the <Link to="/features-solutions">features and solutions</Link>,
              {" "}<Link to="/best-business-software">buyer guide</Link>, and
              {" "}<Link to="/comparisons">comparisons</Link> pages.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section billing-faq-section" aria-labelledby="faq-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">FAQ</p>
            <h2 id="faq-title">Business Software India Questions</h2>
          </div>
          <div className="billing-faq-list">
            {faqs.map(([q, a], index) => {
              const open = openFaq === index;
              return (
                <article className={`billing-faq-item ${open ? "is-open" : ""}`} key={q}>
                  <h3>
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={`india-faq-${index}`}
                      onClick={() => setOpenFaq(open ? null : index)}
                    >
                      <span className="billing-faq-symbol" aria-hidden="true">{open ? "-" : "+"}</span>
                      {q}
                    </button>
                  </h3>
                  <div id={`india-faq-${index}`} className="billing-faq-answer" aria-hidden={!open}>
                    <p>{a}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="billing-final">
        <div className="billing-container">
          <div className="billing-final-panel">
            <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
            <p className="billing-final-kicker">India business software</p>
            <h2>Looking for Business Software in India?</h2>
            <p>
              Explore Auditus-iERP for connected billing, accounting, inventory, POS,
              GST workflows, purchases, sales, customers, suppliers, payments, and reports.
            </p>
            <div className="billing-actions">
              <Link to="/Demo" className="billing-btn billing-btn-primary">
                Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Trial" className="billing-btn billing-btn-secondary">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
